import { SettingsSection, StackWithDividers } from '@components'

import { Divider, Flex, FormItemsGroup, Icon, Space, Spacing, Text } from '@billy/design-system'
import { ReactElement, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { LegalMentionsType, LegalSettingTypes } from '@features/invoicingSettings/modules/compliance'

import { useFormContext } from '../../../../../../../../../../hooks'
import { useInvoiceSettingsModals } from '../../../../contexts/invoiceSettingsModalsContext'
import { InvoiceSettingsFormSchema } from '../../../../types/invoiceSettingsFormSchema'
import { getLegalMentionsDefaultValues } from '../../../../utils/getLegalMentionsDefaultValues'
import { LegalMentionsRow } from './elements/LegalMentionsRow'
import { LegalSettingSwitch } from './elements/LegalSettingSwitch'

const legalMentionsTypes: LegalMentionsType[] = Object.values(LegalMentionsType)
const legalSettingTypes: LegalSettingTypes[] = Object.values(LegalSettingTypes)

export const LegalMentionsSettings = (): ReactElement => {
  const { t } = useTranslation()
  const { setValue: setInvoiceSettingsFormValue, watch } = useFormContext<InvoiceSettingsFormSchema>()
  const { legalMentionsModal } = useInvoiceSettingsModals()

  const { legalMentionsDetails, openModal: openLegalMentionsModal, shouldUpdateForm } = legalMentionsModal

  const legalMentions = watch('legalMentions')

  const handleEditButtonClick = useCallback(
    (legalMentionsType: LegalMentionsType) => {
      openLegalMentionsModal(legalMentionsType, legalMentions ?? getLegalMentionsDefaultValues())
    },
    [legalMentions, openLegalMentionsModal],
  )

  useEffect(() => {
    if (legalMentionsDetails && shouldUpdateForm) {
      setInvoiceSettingsFormValue(`legalMentions`, { ...legalMentionsDetails }, { shouldDirty: true })
    }
    // Only re-run when legalMentionsDetails changes to avoid infinite loop with setInvoiceSettingsFormValue
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legalMentionsDetails, shouldUpdateForm])

  return (
    <SettingsSection.Content title={t('settings.organization.external_invoice.legal_mentions.title')}>
      <Flex flexDirection="column">
        <Space size="s24" />
        <FormItemsGroup rowSpacing={Spacing.S16}>
          <StackWithDividers divider={<Divider />}>
            {legalMentionsTypes.map((legalMentionsType) => (
              <LegalMentionsRow
                key={legalMentionsType}
                legalMentionsType={legalMentionsType}
                onEdit={handleEditButtonClick}
              />
            ))}
            {legalSettingTypes.map((legalSettingType) => (
              <LegalSettingSwitch key={legalSettingType} legalSettingType={legalSettingType} />
            ))}
          </StackWithDividers>
          <Divider />
          <Flex gap={Spacing.S8}>
            <Icon icon="eye" size="sm" />
            <Text variant="bodySM" colorVariant="discreet">
              {t('settings.organization.external_invoice.legal_mentions.note')}
            </Text>
          </Flex>
          <Space />
        </FormItemsGroup>
      </Flex>
    </SettingsSection.Content>
  )
}
